import { type ReactNode, useEffect, useId, useRef } from "react";

/**
 * Bottom sheet that closes with the browser/OS back gesture first, so "back"
 * never leaves the app while a sheet is open. Closing with the button pops
 * the history entry it pushed, keeping the stack clean.
 */
export function Sheet({
	title,
	onClose,
	children,
	footer,
}: {
	title: string;
	onClose: () => void;
	children: ReactNode;
	footer?: ReactNode;
}) {
	const titleId = useId();
	const sheetRef = useRef<HTMLElement>(null);
	const closeRef = useRef(onClose);
	closeRef.current = onClose;
	const closedByPop = useRef(false);
	useEffect(() => {
		const sheet = sheetRef.current;
		const trigger =
			document.activeElement instanceof HTMLElement
				? document.activeElement
				: null;
		window.history.pushState(
			{ ...(window.history.state ?? {}), sobidogamSheet: true },
			"",
			window.location.href,
		);
		document.body.classList.add("sheet-open");
		const focusable = () =>
			Array.from(
				sheet?.querySelectorAll<HTMLElement>(
					'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
				) ?? [],
			).filter((element) => element.getClientRects().length > 0);
		focusable()[0]?.focus();
		const onKeyDown = (event: KeyboardEvent) => {
			const dialogs = document.querySelectorAll('.sheet[role="dialog"]');
			if (dialogs[dialogs.length - 1] !== sheet) return;
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopPropagation();
				closeRef.current();
			} else if (event.key === "Tab") {
				const targets = focusable();
				const first = targets[0];
				const last = targets[targets.length - 1];
				if (!first || !last) {
					event.preventDefault();
					sheet?.focus();
					return;
				}
				if (
					event.shiftKey &&
					(document.activeElement === first ||
						!sheet?.contains(document.activeElement))
				) {
					event.preventDefault();
					last.focus();
				} else if (
					!event.shiftKey &&
					(document.activeElement === last ||
						!sheet?.contains(document.activeElement))
				) {
					event.preventDefault();
					first.focus();
				}
			}
		};
		document.addEventListener("keydown", onKeyDown);
		const onPop = () => {
			closedByPop.current = true;
			closeRef.current();
		};
		window.addEventListener("popstate", onPop);
		return () => {
			document.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("popstate", onPop);
			document.body.classList.remove("sheet-open");
			if (!closedByPop.current && window.history.state?.sobidogamSheet)
				window.history.back();
			if (
				trigger?.isConnected &&
				(document.activeElement === document.body ||
					sheet?.contains(document.activeElement))
			)
				trigger.focus();
		};
	}, []);
	return (
		<div className="sheet-backdrop">
			<section
				ref={sheetRef}
				tabIndex={-1}
				className="sheet"
				role="dialog"
				aria-modal="true"
				aria-labelledby={titleId}
			>
				<header className="sheet-head">
					<h2 id={titleId}>{title}</h2>
					<button
						className="sheet-close"
						type="button"
						aria-label="닫기"
						onClick={() => onClose()}
					>
						닫기
					</button>
				</header>
				<div className="sheet-body">{children}</div>
				{footer ? <footer className="sheet-foot">{footer}</footer> : null}
			</section>
		</div>
	);
}
